import { useAuthStore } from '@/features/auth/store/authStore.js';
import { HeatmapCard } from '@/features/home/components/HeatmapCard.js';
import { useMyShelf } from '@/features/library/hooks/useMyShelf.js';

import { BadgeList } from '../components/BadgeList.js';
import { BarChart } from '../components/BarChart.js';
import { DonutChart } from '../components/DonutChart.js';
import { StatsGrid } from '../components/StatsGrid.js';

export function ProfilePage(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const { data } = useMyShelf();
  const done = data?.items.filter((e) => e.status === 'done').length ?? 0;
  const goal = 52;
  const progress = Math.min(100, Math.round((done / goal) * 100));

  return (
    <div className="flex flex-col gap-8">
      {/* Profile header */}
      <header className="flex flex-col items-center gap-4 text-center lg:flex-row lg:items-start lg:gap-6 lg:text-left">
        <div className="flex h-24 w-24 items-center justify-center rounded-full border-4 border-paper bg-gradient-to-br from-rose to-terracotta text-4xl font-semibold text-cream-light shadow-lg shadow-terracotta/20 xl:h-28 xl:w-28">
          {user?.displayName?.charAt(0).toUpperCase() ?? '👤'}
        </div>
        <div>
          <h1 className="font-serif text-3xl font-semibold text-brown md:text-4xl xl:text-5xl">
            {user?.displayName ?? '—'}
          </h1>
          <p className="mt-1 font-script text-lg text-brown-soft xl:text-xl">
            lectrice insatiable ✦
          </p>
          <p className="mt-1 text-sm text-brown-soft">{user?.email ?? '—'}</p>
          <div className="mt-3 flex flex-wrap justify-center gap-2 lg:justify-start">
            <button
              type="button"
              className="rounded-full bg-paper px-4 py-2 text-xs font-semibold text-brown ring-1 ring-cream-dark hover:bg-cream-dark"
            >
              ✏️ Modifier le profil
            </button>
            <button
              type="button"
              className="rounded-full bg-paper px-4 py-2 text-xs font-semibold text-brown ring-1 ring-cream-dark hover:bg-cream-dark"
            >
              ⚙️ Réglages
            </button>
          </div>
        </div>
      </header>

      {/* Main 2-col grid */}
      <div className="grid gap-6 lg:grid-cols-3 lg:gap-8">
        <div className="flex flex-col gap-6 lg:col-span-2 lg:gap-8">
          {/* Challenge card */}
          <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-terracotta to-terracotta-dark p-6 text-cream-light shadow-lg shadow-terracotta/30">
            <div
              aria-hidden
              className="pointer-events-none absolute -bottom-10 -right-10 h-40 w-40 rounded-full bg-cream-light/10"
            />
            <p className="font-script text-lg opacity-90">Challenge 2026</p>
            <p className="mt-1 font-serif text-4xl font-semibold leading-none md:text-5xl">
              {done}
              <span className="text-2xl opacity-70"> / {goal} livres</span>
            </p>
            <p className="mt-2 text-xs opacity-85">
              {progress >= 100
                ? 'Challenge bouclé — chapeau bas 🌟'
                : 'Pile dans les temps — continue comme ça 🌟'}
            </p>
            <div className="mt-4 h-2 overflow-hidden rounded-full bg-cream-light/20">
              <div
                className="h-full rounded-full bg-gradient-to-r from-gold-light to-cream-light"
                style={{ width: `${progress}%` }}
              />
            </div>
          </section>

          <StatsGrid />
          <BarChart />
          <HeatmapCard />
        </div>

        <aside className="flex flex-col gap-6 lg:gap-8">
          <DonutChart />
          <BadgeList />
        </aside>
      </div>
    </div>
  );
}
